<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { Icon } from '@components'
import { toRGB } from '../utilities'

const emit = defineEmits(['update:modelValue', 'iconRightClick', 'iconLeftClick', 'change'])

const state = reactive({})

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputClass: {
        type: String,
    },
    inputStyle: {
        type: String,
    },
    classBefore: {
        type: String,
    },
    classAfter: {
        type: String,
    },
    inputContainerClass: {
        type: String,
    },
    inputMainContainerClass: {
        type: String,
    },
    inputMainContainerStyle: {
        type: String,
    },
    placeholder: {
        type: String,
    },
    type: {
        type: String,
    },
    iconLeft: {
        type: String,
    },
    iconRight: {
        type: String,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    beforePadding: {
        type: Boolean,
        default: false
    },
    afterPadding: {
        type: Boolean,
        default: false
    },
    iconRightClass: {
        type: String
    },
    iconLeftClass: {
        type: String
    },
    min: {
        type: Number,
    },
    max: {
        type: Number,
    },
    step: {
        type: Number,
    },
    maxlength: {
        type: Number,
    },
    minlength: {
        type: Number,
    },
    pattern: {
        type: String,
    },
    inputmode: {
        type: String,
    },
    title: {
        type: String
    },
    autocomplete: {
        type: String,
    },
    autofocus: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    clearable: {
        type: Boolean,
        default: false
    },
    rounded: {
        type: Boolean,
        default: true
    },
    borderStyle: {
        type: String,
        default: 'Border',
        validator: (value) => {
            return ['Border', 'Underline'].includes(value)
        }
    },
    borderColor: {
        type: String,
        default: '#d0d5dd',
    },
    oultineColor: {
        type: String,
        default: '#f4ebff'
    }
})
const input = ref(null)

/**
 * Focuses the input
 */
const focus = () => {
    state.focused = true
    input.value.focus()
}

onMounted(() => {
    if (props.autofocus) {
        focus()
    }
})

const blur = () => { }

/**
 * Gets the value of the input from props.modelValue and sets it to the value of the input and emits an event.
 */
const inputValue = computed({
    get: () => props.modelValue,
    set: (newValue) => {
        emit('update:modelValue', newValue)
        emit('change', newValue)
    }
})

// watch inputValue
// watch(inputValue, (newValue) => {
//     emit('change', newValue)
// })


const isPassword = ref(true)

/**
 * Returns the input type based on the value of the 'type' prop.
 */
const inputType = computed(() => {
    if (props.type === 'password') {
        return isPassword.value ? 'password' : 'text'
    }
    return props.type
})

const isUnderline = computed(() => props.borderStyle == 'Underline')

const outlineStyle = computed(() => {
    if (!state.focused) return

    if (isUnderline.value)`0px 2px 0px 0px ${toRGB(props.oultineColor, 50)};`

    return `0px 0px 0px 4px ${props.oultineColor}};`
})
</script>

<template>
    <div class="input-wrapper" :class="[
        { 'input-wrapper-disabled': props.disabled, '!rounded-none': !props.rounded, '!border-x-transparent !border-t-transparent !rounded-none': isUnderline },
        props.inputMainContainerClass
    ]" :style="{
        'border-color': state.focused ? props.oultineColor : props.borderColor,
        'box-shadow': outlineStyle
    }" v-bind="$attrs">
        <!-- Before slot -->
        <div v-if="$slots.before" class="input-before-slot-wrapper" :class="{ 'before-slot-padding': props.beforePadding, [props.classBefore]: props.classBefore }">
            <slot name="before"></slot>
        </div>

        <!-- Main input wrapper -->
        <div class="main-input-wrapper outline-none" tabindex="1" :class="[
            { 'rounded-l-lg': !$slots.before, 'rounded-r-lg': !$slots.after, '!rounded-none': !props.rounded, '!px-0': isUnderline },
            inputContainerClass
        ]" @click.prevent="focus">
            <!-- Icon left -->
            <Icon @click.prevent="emit('iconLeftClick')" v-if="props.iconLeft" :name="props.iconLeft" class="text-[#667085]" :class="iconLeftClass" />

            <!-- Input -->
            <input :required="props.required" ref="input" class="input-field placeholder:text-[#667085]" :type="inputType" :class="inputClass" :style="inputStyle" :placeholder="props.placeholder" v-model="inputValue" @focus="state.focused = true" @blur="state.focused = false" :title="props.title" :readonly="props.readonly" :disabled="props.disabled" :min="props.min" :max="props.max" :step="props.step" :maxlength="props.maxlength" :minlength="props.minlength" :pattern="props.pattern" :inputmode="props.inputmode" :autocomplete="props.autocomplete" :autofocus="props.autofocus" />

            <!-- Icon right -->
            <Icon @click.prevent="emit('iconRightClick')" v-if="props.iconRight" :name="props.iconRight" class="text-[#667085]" :class="iconRightClass" />
            <Icon v-if="props.clearable && inputValue" name="xClose" class="cursor-pointer text-[#667085]" @click="inputValue = ''" />
            <Icon v-if="props.type === 'password'" @click.prevent="isPassword = !isPassword" :name="isPassword ? 'eyeOff' : 'eye'" class="text-[#667085] cursor-pointer" :class="iconRightClass" />
        </div>

        <!-- After slot -->
        <div v-if="$slots.after" class="input-after-slot-wrapper" :class="{ 'after-slot-padding': props.afterPadding, [props.classAfter]: props.classAfter }">
            <slot name="after"></slot>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.input-wrapper {
    display: flex;
    align-items: center;
    border-width: 1px;
    font-weight: 400;
    line-height: 1.25rem;
    background-color: #ffffff;
    border-color: #D0D5DD;
    border-radius: 0.5rem;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);

    &.input-wrapper-disabled {
        background-color: #F3F4F6;
        pointer-events: none;
    }

    .input-before-slot-wrapper {
        border-right-width: 1px;
        border-color: #E5E7EB;
        width: fit-content;
        height: 100%;
        white-space: nowrap;

        &.before-slot-padding {
            padding-top: 0.625rem;
            padding-bottom: 0.625rem;
            padding-left: 0.875rem;
            padding-right: 0.875rem;

        }
    }

    .main-input-wrapper {
        display: flex;
        position: relative;
        padding-top: 0.625rem;
        padding-bottom: 0.625rem;
        padding-left: 0.875rem;
        padding-right: 0.875rem;
        gap: 0.5rem;
        align-items: center;
        width: 100%;
        transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 100ms;

        .input-field {
            padding-top: 0;
            padding-bottom: 0;
            margin: 0;
            outline-style: none;
            width: 100%;
            height: 100%;
            font-size: 1rem;
            line-height: 1.5rem;
            background-color: transparent;
            color: #101828;

        }
    }

    .input-after-slot-wrapper {
        border-left-width: 1px;
        border-color: #E5E7EB;
        width: fit-content;
        height: 100% !important;
        white-space: nowrap;

        &.after-slot-padding {
            padding-top: 0.625rem;
            padding-bottom: 0.625rem;
            padding-left: 0.875rem;
            padding-right: 0.875rem;
        }
    }
}
</style>
